import * as React from 'react';
import classnames from 'classnames';
import { ICoreContext, Image } from '@msdyn365-commerce/core';
import { ArrayExtensions, createInventoryAvailabilitySearchCriteria, DeliveryMode, IProductInventoryInformation, mapAggregatedProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { ProductSearchResultExt } from '../../../shared-utils/types';
import { cssCapitalize, getProductPageUrlWithCategoryPathSync, isProductInCategory } from '../../../shared-utils/common-utils';
import { applyAttributesToProduct } from '../../../shared-utils/attributes';
import { getActivePricesAsync, getByIdsAsync, getCategoryPathsAsync, getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { getProductUnitOfMeasureOptionsAsync } from '../../../actions/get-product-unit-of-measure-options.action';
import { getConversionText, getKilogramConversion, getLiterPrice, getNonBreakoffUOM, getPcsConversion, getRoundedFactor } from '../../../shared-utils/unit-conversion-utils';
import QuantityInput from '../../../components/common/quantity-input';
import PriceComponent from '../../../themes/dlvry-adventureworks/views/components/price.component';
import { humanizeUnitOfMeasure } from '../../../components/unit-of-measure';
import { getHideConversionInCategories } from '../../../settings/app.settings.utils';
import { Availability } from '../../../components/dropdown/availability';
import { multiplyPriceBy } from '../../../shared-utils/price-utils';
import { ensureBaseImageUrl } from '../../../shared-utils/image-utilities';

export interface IReplacementProductsProps {
    id: string;
    typeName: string;
    context: ICoreContext;
    replacements: ProductSearchResultExt[];
}

const ReplacementProducts: React.FC<IReplacementProductsProps> = (props) => {
    const baseClassName = 'ms-buybox__replacement-modal';
    const { id, typeName, context, replacements } = props;

    const [products, setProducts] = React.useState<ProductSearchResultExt[]>([]);
    const [availabilities, setAvailabilities] = React.useState<IProductInventoryInformation[]>([]);
    const [simpleProducts, setSimpleProducts] = React.useState<SimpleProduct[]>([]);
    const [unitConversions, setUnitConversions] = React.useState<UnitOfMeasureConversion[]>([]);

    const [breakoffStates, setBreakoffStates] = React.useState<{ [key: number]: boolean }>({});
    const [retrieved, setRetrieved] = React.useState(false);

    React.useEffect(() => {
        void (async () => {
            const ids = ArrayExtensions.unique(replacements.map(r => r.RecordId));

            if (ids.length === 0) {
                return;
            }

            const simpleProductsPromise = getByIdsAsync(
                { callerContext: context.actionContext },
                context.request.apiSettings.channelId,
                ids,
                null,
                context.request.apiSettings.catalogId
            );
            const categoryPathsPromise = getCategoryPathsAsync(
                { callerContext: context.actionContext },
                context.request.apiSettings.channelId,
                context.request.apiSettings.catalogId,
                ids.map(id => ({ ProductId: id }))
            );
            const pricesPromise = getActivePricesAsync(
                { callerContext: context.actionContext },
                { CatalogId: context.request.apiSettings.catalogId, ChannelId: context.request.apiSettings.channelId },
                ids,
                new Date(),
                null,
                undefined,
                true
            );
            const availabilitiesPromise = getEstimatedAvailabilityAsync(
                { callerContext: context.actionContext },
                createInventoryAvailabilitySearchCriteria(
                    context.actionContext,
                    ids,
                    true,
                    undefined,
                    undefined,
                    DeliveryMode.shipping
                )
            );
            const unitOfMeasureConversionsPromise = getProductUnitOfMeasureOptionsAsync({ callerContext: context.actionContext }, ids);

            const [
                simpleProducts,
                categoryPaths,
                prices,
                availabilities,
                unitConversions
            ] = await Promise.all([simpleProductsPromise, categoryPathsPromise, pricesPromise, availabilitiesPromise, unitOfMeasureConversionsPromise]);

            const newProducts = replacements.map(replacement => {
                const newProduct = { ...replacement };
                const categoryPath = categoryPaths.find(cp => cp.ProductId === replacement.RecordId)?.CategoryPath;
                const price = prices.find(p => p.ProductId === replacement.RecordId);
                replacement.ProductPrice = price;
                applyAttributesToProduct(context.request, newProduct, newProduct.AttributeValues, categoryPath, true);
                return newProduct;
            });
            if (availabilities.ProductWarehouseInventoryAvailabilities) {
                const shippingInventory = mapAggregatedProductInventoryInformation(context.actionContext, availabilities);
                shippingInventory.forEach(x => x.deliveryType = DeliveryMode.shipping);
                setAvailabilities(shippingInventory);
            }
            setSimpleProducts(simpleProducts);
            setUnitConversions(unitConversions);
            setProducts(newProducts);
            setRetrieved(true);
        })();
    }, [products.map(p => p.RecordId).join(',')]);

    React.useEffect(() => {
        const productsWithAttributes = replacements.map(replacement => {
            const newProduct = { ...replacement };
            applyAttributesToProduct(context.request, newProduct, newProduct.AttributeValues, undefined, false);
            return newProduct;

        });
        setProducts(productsWithAttributes);
    }, [replacements]);

    const toggleBreakoff = (recordId: number, newValue: boolean) => {
        setBreakoffStates(prev => ({ ...prev, [recordId]: newValue }));
    };

    const randomCategoryWidths = React.useMemo(() => replacements.reduce((acc, replacement) => {
        acc[replacement.RecordId] = Math.floor(Math.random() * (130 - 50 + 1)) + 50;
        return acc;
    }, {} as Record<string, number>), [replacements]);

    const randomConversionWidths = React.useMemo(() => replacements.reduce((acc, replacement) => {
        acc[replacement.RecordId] = Math.floor(Math.random() * (100 - 40 + 1)) + 40;
        return acc;
    }, {} as Record<string, number>), [replacements]);

    const randomBasePriceWidths = React.useMemo(() => replacements.reduce((acc, replacement) => {
        acc[replacement.RecordId] = Math.floor(Math.random() * (96 - 65 + 1)) + 65;
        return acc;
    }, {} as Record<string, number>), [replacements]);

    const randomExtraPriceWidths = React.useMemo(() => replacements.reduce((acc, replacement) => {
        acc[replacement.RecordId] = Math.floor(Math.random() * (100 - 90 + 1)) + 90;
        return acc;
    }, {} as Record<string, number>), [replacements]);

    return (
        <ul className={`${baseClassName}__products`}>
            {products.map(replacement => {
                const productUrl = getProductPageUrlWithCategoryPathSync(context.actionContext, replacement, replacement.CategoryPaths);
                const breakoff = breakoffStates[replacement.RecordId] || false;
                const availability = availabilities?.find(a => a.ProductAvailableQuantity.ProductId === replacement.RecordId);
                const simpleProduct = simpleProducts.find(sp => sp.RecordId === replacement.RecordId);
                const unitConversion = unitConversions.filter(uc => uc.ProductRecordId === replacement.RecordId);
                const hideConversionInCategories = getHideConversionInCategories(context);
                const dontShowKgPrice = isProductInCategory(replacement, hideConversionInCategories);
                const kilogramConversion = !dontShowKgPrice && unitConversion && getKilogramConversion(simpleProduct?.DefaultUnitOfMeasure, unitConversion);
                const pcsConversion = unitConversion && getPcsConversion(unitConversion, simpleProduct?.DefaultUnitOfMeasure);

                const additionalInformationClassName = (replacement.AlcoholProduct && replacement.ImporterGrouping) && 'importer-grouping'
                    || replacement.Brand && 'brand'
                    || replacement.Manufacturer && 'manufacturer'
                    || replacement.Producer && 'producer';
                const additionalInformationText = (replacement.AlcoholProduct && replacement.ImporterGrouping)
                    || replacement.Brand
                    || replacement.Manufacturer
                    || replacement.Producer;

                // If product information has not yet been retrieved, create a placeholder price to show the shimmer effect.
                // If product information has been retrieved but price and/or simple product is missing, just set to undefined and price wont render
                const price = !retrieved
                    ? { CustomerContextualPrice: 0 }
                    : !replacement.ProductPrice || !simpleProduct
                    ? undefined
                    : (!breakoff && simpleProduct?.Behavior?.MultipleOfQuantity
                        ? multiplyPriceBy(replacement.ProductPrice, simpleProduct.Behavior.MultipleOfQuantity)
                        : replacement.ProductPrice);
                const literPrice = retrieved &&
                    price &&
                    getLiterPrice(unitConversion, simpleProduct?.DefaultUnitOfMeasure || replacement.DefaultUnitOfMeasure, price);

                return (
                    <li key={replacement.RecordId} className={`${baseClassName}__product-info`}>
                        <div className={`${baseClassName}__product-info__image`}>
                            <Image
                                src={ensureBaseImageUrl(context, replacement?.PrimaryImageUrl)}
                                altText={replacement?.Name}
                                requestContext={context.request}
                                gridSettings={context.request.gridSettings!}
                                imageSettings={{
                                    mode: 1,
                                    quality: 80,
                                    viewports: {
                                        xs: { h: 256, w: 256 },
                                        sm: { h: 128, w: 128 },
                                        md: { h: 128, w: 128 },
                                        lg: { h: 128, w: 128 },
                                        xl: { h: 128, w: 128 }
                                    }
                                }}
                                loadFailureBehavior='empty'
                            />
                        </div>
                        <div className={`${baseClassName}__product-info__details`}>
                            <div className={`${baseClassName}__product-info__details__info`}>
                                <div
                                    className={classnames(`${baseClassName}__product-info__details__info__category`, { retrieving: !retrieved })}
                                    data-shimmer-width={randomCategoryWidths[replacement.RecordId]}
                                >
                                    {replacement.Category}
                                </div>
                                <a className={`${baseClassName}__product-info__details__info__name`} href={productUrl}>
                                    {cssCapitalize(replacement?.Name, context.request.locale)}
                                </a>
                                <div className={`${baseClassName}__product-info__details__info__itemid`}>
                                    {replacement?.ItemId}
                                </div>
                                {additionalInformationText && (
                                    <div className={`${baseClassName}__product-info__details__info__${additionalInformationClassName}`}>
                                        {additionalInformationText}
                                    </div>
                                )}
                                <div
                                    className={classnames(`${baseClassName}__product-info__details__info__conversion`, { retrieving: !retrieved })}
                                    data-shimmer-width={randomConversionWidths[replacement.RecordId]}
                                >
                                    {getConversionText(context, simpleProduct?.DefaultUnitOfMeasure || replacement.DefaultUnitOfMeasure, unitConversion, simpleProduct, false, replacement)}
                                </div>
                            </div>
                            <div className={`${baseClassName}__product-info__details__prices`} data-shimmer-width={randomBasePriceWidths[replacement.RecordId]}>
                                {price && (
                                    <PriceComponent
                                        context={context}
                                        className={classnames(`${baseClassName}__product-info__details__prices__base`, { retrieving: !retrieved })}
                                        data={{ price }}
                                        id={id}
                                        typeName={typeName}
                                    />
                                )}
                                {!retrieved && (
                                    <div className={`${baseClassName}__product-info__details__prices__extra retrieving`} data-shimmer-width={randomExtraPriceWidths[replacement.RecordId]}>
                                        <PriceComponent
                                            context={context}
                                            data={{ price: { CustomerContextualPrice: 1 } }}
                                            id={id}
                                            typeName={typeName} />
                                        &nbsp;
                                        pr. {humanizeUnitOfMeasure(context, replacement?.DefaultUnitOfMeasure).toLowerCase()}
                                    </div>
                                )}
                                {replacement.ProductPrice?.CustomerContextualPrice && (!!(!breakoff && simpleProduct?.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1)) && (
                                    <div className={`${baseClassName}__product-info__details__prices__extra`}>
                                        <PriceComponent
                                            context={context}
                                            data={{ price: { CustomerContextualPrice: replacement.ProductPrice.CustomerContextualPrice } }}
                                            id={id}
                                            typeName={typeName} />
                                        &nbsp;
                                        pr. {humanizeUnitOfMeasure(context, replacement?.DefaultUnitOfMeasure).toLowerCase()}
                                    </div>
                                )}
                                {replacement.ProductPrice?.CustomerContextualPrice && pcsConversion && (
                                    <div className={`${baseClassName}__product-info__details__prices__extra`}>
                                        <PriceComponent
                                                context={context}
                                                data={{ price: { CustomerContextualPrice: replacement.ProductPrice.CustomerContextualPrice / (pcsConversion.Factor || 1) } }}
                                                id={id}
                                                typeName={typeName} />
                                        &nbsp;
                                        pr. stk
                                    </div>
                                )}
                                {replacement.ProductPrice?.CustomerContextualPrice && kilogramConversion && getRoundedFactor(kilogramConversion) > 0 && (
                                    <div className={`${baseClassName}__product-info__details__prices__extra`}>
                                        <PriceComponent
                                            context={context}
                                            data={{ price: { CustomerContextualPrice: replacement.ProductPrice.CustomerContextualPrice / getRoundedFactor(kilogramConversion) } }}
                                            id={id}
                                            typeName={typeName} />
                                        &nbsp;
                                        pr. kg
                                    </div>
                                )}
                                {literPrice && (
                                    <div className={`${baseClassName}__product-info__details__prices__extra`}>
                                        <PriceComponent
                                                context={context}
                                                data={{ price: { CustomerContextualPrice: literPrice } }}
                                                id={id}
                                                typeName={typeName} />
                                        &nbsp;
                                        pr. liter
                                    </div>
                                )}
                            </div>
                            <Availability
                                baseClassName={`${baseClassName}__product-info__details`}
                                context={context}
                                availability={availability}
                            />
                            <div className={`${baseClassName}__product-info__details__quantity`}>
                                <QuantityInput
                                    mode='view'
                                    allowBreakoffToggleInReadOnly
                                    context={context}
                                    product={simpleProduct}
                                    extProduct={replacement}
                                    quantity={breakoff ? 1 : simpleProduct?.Behavior?.MultipleOfQuantity || 1}
                                    breakoff={breakoff}
                                    nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, unitConversion)}
                                    onBreakoffChange={(newValue) => toggleBreakoff(replacement.RecordId, newValue)}
                                    onQuantityChange={() => void 0}
                                    unitConversions={unitConversion}
                                />
                            </div>
                            <div className={`${baseClassName}__product-info__details__actions`}>
                                <a className={`${baseClassName}__product-info__details__actions__link`} href={productUrl}>
                                    Se produkt
                                </a>
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

export default ReplacementProducts;
